import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'REGÍSTRESE AHORA',
  heroCopy: '¡Buena elección con su compra!',
  heroSubCopy1:
    'Registre ahora su electrodoméstico en un plazo de 60 días para desbloquear sus beneficios gratuitos',
  benefitTicks: [
    '10 años de servicios de reparación con repuestos gratuitos*',
    'Piezas originales de Beko',
    'Protección adicional opcional frente a daños accidentales y averías',
  ],
  heroDisclaimer:
    'Las piezas incluyen motores, bombas, placas de control y otros componentes elegibles',
  registration: 'Llame ahora para registrarse',
  ctaPhoneText: 'Llamar al 917 885 126',
  ctaPhoneLink: 'tel:+34917885126',
  phoneSubCopy: 'Líneas abiertas de lunes a viernes de 9:00 a 18:00.',
  onlineCopy: 'Regístrese online en pocos minutos',
  ctaOnlineText: 'Regístrese online',
  sixtyDayHeader: 'Regístrese en un plazo de 60 días',
  sixtyDayBody:
    'Registre sus 10 años de servicios de reparación con repuestos gratuitos antes de que venza el plazo',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP1 COPY V2_SEPT 2026 2.xlsx (BEKO tab)
// as applied in the approved mockups (claude-notes/lp-hybrid-lp1). Every line
// matches Beko control or V1. Dials the same number as V1: `ctaPhoneLink` is
// V1's (undefined = the base link). The `**` on the first heading is appended
// by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Regístrese ahora',
  offerHeadline: ['10 AÑOS', 'de servicios de reparación', 'con repuestos GRATUITOS*'],
  sixtyDayHeader:
    'Registro necesario dentro de los primeros 60 días de la compra del electrodoméstico',
  registration: 'Llame ahora para registrarse',
  ctaPhoneText: 'Llame al 917 697 971',
  // Own dial link (not V1's): V1 Spain may use the control number on prod (PR #42/#43,
  // carrier outage 29 Sep); the hybrid keeps its own number.
  ctaPhoneLink: 'tel:+34917697971',
  benefitsHeader: 'Disfrute ahora sus beneficios gratuitos:',
  benefitTicks: [
    '10 años de servicios de reparación con repuestos gratuitos',
    'Piezas originales de Beko',
    'Protección adicional opcional frente a daños accidentales y averías',
  ],
  phoneSubCopy: 'El horario de atención es de lunes a viernes de 9:00 a 18:00.',
  repeatHeading: '¿Listo para registrarse?',
  ctaPhoneRepeatText: 'Llame ahora al 917 697 971',
};

export const data: LocaleData = {
  banner: 'Registre su producto para',
  heroNumber: '10',
  heroCopy: 'años de servicios de reparación con repuestos GRATUITOS*',
  heroSubCopy1: '<b>Registro necesario</b>',
  heroSubCopy2: 'dentro de los primeros 60 días de la compra del electrodoméstico',
  registration: 'Llame ahora y registre**',
  ctaPhoneText: '917 885 126',
  ctaPhoneLink: 'tel:917885126',
  phoneSubCopy:
    'Registrarse por teléfono es rápido y le proporciona información adicional sobre la garantía del fabricante y los 10 años de servicios de reparación con repuestos GRATUITOS*. La línea telefónica de registro está abierta de Lunes a Viernes de 09:00 a 18:00.',
  onlineCopy: '',
  ctaOnlineText: '',
  ctaOnlineLink: 'https://beko.register-appliance.com/RegistroPorMarca?country=ES&language=es',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Regístrese en línea',
      linesClosedMessage:
        'Nuestras líneas telefónicas están cerradas actualmente, pero puede registrarse online en cualquier momento',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG, sucursal en España, registrará su electrodoméstico. Domestic & General Insurance Europe AG, sucursal en España, le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar, si usted está interesado. Para conocer cómo Beko Spain Electronics SL y Domestic & General Insurance Europe AG, sucursal en España, tratan sus datos visite <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
    // LP3 COPY V1_JULY 2026.xlsx (BEKO tab; ** added per master sheet).
    'link-only': {
      registration:
        'Registre ahora su electrodoméstico para desbloquear sus beneficios gratuitos**',
      onlineCopy:
        'Nuestras líneas telefónicas están cerradas actualmente, pero puede registrarse online en cualquier momento',
      ctaOnlineText: 'Regístrese en línea',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG, sucursal en España, registrará su electrodoméstico. Domestic & General Insurance Europe AG, sucursal en España, le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar, si usted está interesado. Para conocer cómo Beko Spain Electronics SL y Domestic & General Insurance Europe AG, sucursal en España, tratan sus datos visite <0>www.domesticandgeneral.com/beko</0>',
      ],
      phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
    },
  },
  benefitsHeader: 'Sus beneficios al registrarse:',
  benefits: [
    'Conservar su justificante de compra en formato digital',
    'Recibir importantes actualizaciones de seguridad del dispositivo',
    'Realizar sus reparaciones por el servicio oficial Beko con recambios originales',
  ],
  footerLinks: [
    { text: 'Informaciones legales', link: '/terms' },
    { text: 'Política de Privacidad', link: 'https://www.domesticandgeneral.com/beko' },
    { text: 'Cookies', link: '/cookies' },
  ],
  footerCopy:
    'Este sitio web es propiedad de Domestic & General Group Limited y está operado por Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Germany. Domestic & General Insurance Europe AG está autorizada y regulada por Autoridad Federal de Supervisión Financiera (BaFin) en Alemania.',
  heroAsterix: [
    '* Registre su producto dentro de los primeros 60 días desde la compra del electrodoméstico y, una vez vencida la garantía legal, en caso de avería, durante 10 años a partir de la fecha de vencimiento de la garantía legal, tendrá la opción de contratar servicios de reparación con una tarifa fija que no incluye el coste de las piezas de repuesto. Servicio proporcionado por Beko Spain Electronics SL. El precio del servicio de reparación varía con el tiempo. Para consultar el precio actual del servicio, llame al número de Domestic & General Insurance Europe AG, sucursal en España, socio de Beko Spain Electronics SL, o visite el sitio web <0>https://www.beko.com/es-es/soporte/centro-de-ayuda</0>. Se aplican términos y condiciones. Disponible solo en España. Antes de registrarse, lea los términos y condiciones detallados del servicio disponibles en <0>https://www.beko.com/es-es/soporte/centro-de-ayuda</0>',
  ],
  heroAsterixLinks: ['https://www.beko.com/es-es/soporte/centro-de-ayuda'],
  phoneNumberAsterix: [
    '** Número de teléfono fijo operado por Domestic & General Insurance Europe AG, sucursal en España. Se aplica la tarifa de llamadas locales habitual de su proveedor de telefonía. Domestic & General Insurance Europe AG, sucursal en España, le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar, si usted está interesado. Para conocer cómo Beko Spain Electronics SL y Domestic & General Insurance Europe AG, sucursal en España, tratan sus datos visite <0>https://www.domesticandgeneral.com/beko</0>',
  ],
  phoneNumberAsterixLinks: ['https://www.domesticandgeneral.com/beko'],
  additionalSmallPrint: '',
  languageSwitcherLabel: 'País',
  languageSwitcherPlaceholder: 'Seleccione su país',
  languageList: [
    { label: 'Alemania', value: 'de-DE' },
    { label: 'Francia', value: 'fr-FR' },
    { label: 'Italia', value: 'it-IT' },
    { label: 'España', value: 'es-ES' },
    { label: 'Países Bajos', value: 'nl-NL' },
    { label: 'Bélgica (francés)', value: 'be-FR' },
    { label: 'Bélgica (holandés)', value: 'be-NL' },
  ],
  cookiePolicy: {
    title: 'Lista de cookies',
    sections: [
      {
        heading: 'Lista de cookies',
        description: [
          'Una cookie es un fragmento pequeño de datos (archivos de texto) que un sitio web, cuando es visitado por un usuario, le pregunta a su navegador para almacenarse en su dispositivo y así recordar información acerca de usted, como por ejemplo la preferencia de idioma o su información para iniciar sesión. Estas cookies son establecidas por nosotros, y se llaman cookies de primeras partes. También usamos cookies de terceras partes (que son cookies de un dominio diferente al dominio del sitio web que está visitando) para nuestros estudios de anuncios y marketing. Específicamente, usamos las cookies y otras tecnologías de seguimiento para los siguientes propósitos:',
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Informaciones legales',
    content: [
      {
        heading: 'Aviso legal y términos de uso',
        paragraphs: ['Aviso Legal', 'Términos de uso'],
      },
      {
        heading: 'Aviso Legal',
        paragraphs: [
          'Le informamos de que esta página web es titularidad de Domestic and General Insurance Europa AG, sucursal en España, con CIF W2765940H, inscrita en el Registro Administrativo de Entidades Aseguradoras de la Dirección General de Seguros y Fondos de Pensiones, bajo la clave E0240 y en el Registro Mercantil de Madrid al tomo 38974, libro 0, folio 13, hoja M692570.',
          'Domestic and General Insurance Europe AG está autorizada por la Autoridad Federal de Supervisión Financiera Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin), con domicilio en Graurheindorfer Str. 108, 53117 Bonn.',
          'Puede ponerse en contacto con nosotros a través de los siguientes medios:',
          '- Correo electrónico soporte.clientes@domesticandgeneral.com,',
          '- Número de teléfono 916 350 804, o',
          'El sitio web de Domestic & General Group Limited es: http://www.domesticandgeneral.com',
        ],
      },
      {
        heading: 'Términos y condiciones del sitio web',
        paragraphs: [
          'Este Sitio Web está proporcionado por el grupo Domestic & General en nombre de Beko Spain Electronics SL. Al acceder a este Sitio Web o hacer uso de él, usted acepta los siguientes términos y condiciones. Si no acepta los presentes términos y condiciones de uso y no tiene intención de regirse por ellos, no debe utilizar el sitio web y debe abandonarlo inmediatamente.',
        ],
      },
      {
        heading: '1. Definiciones',
        paragraphs: [
          '"D&G" hace referencia a Domestic & General Insurance Europe AG, sucursal en España, una empresa inscrita en el Registro Mercantil de Madrid, hoja Nº 37, folio 218, tomo 2.084, y en el registro de Entidades Aseguradoras de la DGSFP, bajo la clave E0240, con domicilio social en C/Julian Camarillo 4, Módulo B 3ª planta, 28037 Madrid.',
          '"Cliente" hace referencia a Beko Spain Electronics SL',
          '"Nosotros/nos" hace referencia a Domestic & General Insurance Europe AG, sucursal en España, y/o a cliente, según sea el caso.',
          '"Sitio Web" hace referencia al sitio de registro del cliente, que es propiedad del Cliente y que está administrado en su nombre y representación por D&G.',
          '"Usted" hace referencia al usuario de este Sitio Web',
        ],
      },
      {
        heading: '2. Uso del sitio web',
        paragraphs: [
          'Usted puede navegar por este Sitio Web y reproducir algunos extractos imprimiéndolos o descargándolos en un disco duro para su uso personal. Queda prohibido:',
          'Copiar, distribuir, alterar o modificar de alguna forma o utilizar de otra manera cualquier material contenido en el Sitio Web, salvo lo permitido anteriormente. Copiar, distribuir o utilizar de otra forma cualquier material procedente del Sitio Web para algún fin comercial.',
          'Eliminar ningún aviso de derechos de autor, de marca comercial u otros avisos de derechos de propiedad intelectual contenidos en el material original procedente de cualquier material copiado o impreso del Sitio Web.',
          'Tenga en cuenta que todos los derechos de autor, así como otros derechos de propiedad intelectual sobre cualquier material contenido en este Sitio Web son propiedad de D&G. El Sitio Web contiene marcas comerciales, incluidos el nombre y el logotipo del Cliente, que pertenecen al Cliente, y el nombre y el logotipo de D&G, que pertenecen a D&G. Ningún logotipo o imagen que se encuentre en el Sitio Web se puede retransmitir o copiar, total o parcialmente, salvo que esté autorizado expresamente por el propietario. La información concerniente a cómo utilizaremos los datos personales que recopilamos en este Sitio Web se proporciona en la Política de Privacidad del Sitio Web.',
        ],
      },
      {
        heading: '3. Contenido',
        paragraphs: [
          'Toda la información presente en este Sitio Web está sujeta a cambios sin previo aviso. No nos comprometemos a actualizar la información en todo momento. Si bien hemos tomado las precauciones razonables para verificar la exactitud y vigencia de los materiales contenidos en este Sitio Web, no ofrecemos ninguna garantía explícita o implícita ni representación sobre cualquiera de los contenidos de este Sitio Web, incluidas todas las garantías y condiciones de comercialización implícitas, idoneidad para un fin determinado, titularidad y no vulneración. Toda la información se proporciona "tal cual" y no aceptamos ninguna responsabilidad por daños o perjuicios que se puedan derivar de la utilización de informaciones o consejos contenidos en este Sitio Web. Asimismo, tampoco seremos responsables de ningún posible error u omisión en los materiales contenidos en este Sitio Web. Por otra parte, no garantizamos en absoluto que el Sitio Web vaya a estar disponible de forma ininterrumpida, y no aceptamos ninguna responsabilidad por los daños o perjuicios derivados de dicha indisponibilidad.',
        ],
      },
      {
        heading: '4. Enlaces a otros sitios web externos',
        paragraphs: [
          'El Sitio Web puede incluir enlaces a otros sitios web. Dichos enlaces se proporcionan para ayudarle a encontrar información adicional de una manera rápida y sencilla. Al hacer clic en un enlace, puede que salga de nuestro Sitio Web y del contenido controlado por D&G, y enlazará con el sitio web de un tercero externo ("sitio web vinculado"). D&G no tiene ninguna influencia sobre el contenido del sitio web vinculado y carece de responsabilidad sobre la información proporcionada en el sitio web vinculado. La supervisión constante de los contenidos del sitio web vinculado, si no existen indicios concretos de infracciones, no es exigible a D&G; sin embargo, D&G eliminará el enlace a un sitio web vinculado cuando tenga conocimiento de que se proporciona contenido ilegal o que infrinja la ley de alguna otra forma en el sitio web vinculado. No aceptamos ninguna responsabilidad por el contenido o uso de dichos sitios web vinculados.',
        ],
      },
      {
        heading: '5. Quienes somos',
        paragraphs: ['El Sitio Web está proporcionado por D&G en nombre del cliente.'],
      },
      {
        heading: '6. Servicios de registro de productos',
        paragraphs: [
          'Domestic & General Insurance Europe AG, sucursal en España ofrece servicios de registro de productos en nombre del cliente.',
        ],
      },
      {
        heading: '7. Información importante sobre el proveedor',
        paragraphs: [
          'La propuesta y celebración de contratos de seguro (conocido como actividades de mediación de seguros) en este Sitio Web se lleva a cabo por Domestic & General Insurance Europe AG, sucursal en España. Todos los contratos de seguro adquiridos en este Sitio Web están sujetos a los términos y condiciones de la póliza que rigen su uso. Usted deberá leer los términos y condiciones de esta póliza junto con cualquiera de los términos incluidos en este Sitio Web.',
          'Domestic & General Insurance Europe AG, sucursal en España pertenece a la entidad aseguradora alemana Domestic & General Insurance Europe AG con domicilio social en Hagenauer Straße 44, 65203 Wiesbaden (Alemania). La Sucursal en España, con CIF: W2765940H, se encuentra debidamente inscrita en el Registro Administrativo de Entidades Aseguradoras de la DGSFP, bajo la clave E0240, y tiene su domicilio en C/ Julián Camarillo 4, modulo B, 3ª Planta,28037 Madrid. El Estado miembro a quien corresponde el control de la Entidad Aseguradora es Alemania y, dentro de dicho Estado, la Autoridad a quien corresponde dicho control es la Autoridad Federal de Supervisión Financiera Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin), con domicilio en Graurheindorfer Str. 108, 53117 Bonn.',
        ],
      },
      {
        heading: '8. Responsabilidad',
        paragraphs: [
          'No nos hacemos responsables del fallo, mal funcionamiento o no disponibilidad del Sitio Web, a menos que sea consecuencia de nuestro incumplimiento doloso o de un fraude por nuestra parte. No nos hacemos responsables de ninguna clase de daño o perjuicio especial, indirecto o fortuito sufrido por usted, (ni de ninguna pérdida de beneficios, de oportunidad de negocio o de fondo de comercio), como consecuencia del uso que usted haga del Sitio Web o del fallo o mal funcionamiento de este. Podemos suspender, retirar, limitar o cancelar el uso del Sitio Web en cualquier momento.',
        ],
      },
      {
        heading: '9. Legislación aplicable',
        paragraphs: [
          'Los presentes términos y condiciones y el uso que usted haga de este Sitio Web se rigen por las leyes españolas.',
        ],
      },
      {
        heading: '10. Límites territoriales',
        paragraphs: [
          'El Sitio Web está dirigido a las personas que acceden a él y lo utilicen en los territorios de Alemania, Austria, Bélgica, España, Francia, Irlanda, Polonia o Portugal.',
        ],
      },
      {
        heading: '11. Virus',
        paragraphs: [
          'Tomamos las medidas razonables para garantizar que este Sitio Web no contenga ningún virus. No obstante, no nos hacemos responsables de los daños causados por algún virus u otra tecnología como resultado del uso que usted haga de este Sitio Web o de la descarga de cualquier material publicado en él o en cualquier sitio web vinculado a este.',
        ],
      },
      {
        heading: '12. Consejos y recomendaciones',
        paragraphs: [
          'Nada de lo escrito en este Sitio Web se debe tomar como consejo o recomendación. Para decidir la adquisición de un contrato de seguro en este Sitio Web, no debe basarse únicamente en la información contenida en las páginas de este sitio. Debe leer cuidadosamente la documentación y/o los términos y condiciones de la póliza en relación con cualquier producto específico antes de adquirir el contrato de seguro.',
        ],
      },
      {
        heading: '13. Contacto',
        paragraphs: [
          'Si tiene alguna duda sobre este sitio web o desea ponerse en contacto con nosotros, puede hacerlo enviándonos un correo electrónico a soporte.clientes@domesticandgeneral.com. Gracias por visitar este sitio web. Tenga presente que D&G puede actualizar los términos y condiciones anteriores, así como los avisos, de manera periódica; por lo tanto, le recomendamos que los revise cada vez que vuelva a visitar el Sitio Web.',
        ],
      },
    ],
  },
};
